import type { BalanceTimelineResponse } from '@tv-casino/shared';
import { formatSignedChips } from '../lib/format.js';
import {
  sparkLegendOf,
  sparklineOf,
  sparkMarksOf,
  sparkRunsOf,
  type SparkLegendItem,
  type SparkMark,
  type SparkPoint,
  type SparkRun,
} from './memberMarketModel.js';

/**
 * The pure view-model behind the owner's session tape: every member's night
 * as its own sparkline in the market-desk style — coloured by game, high and
 * low pinned — under a quote strip across every series. The server already
 * collapsed each round's stake and payout into a single point.
 */

export interface BalanceChartLineVM {
  memberId: string;
  name: string;
  points: SparkPoint[];
  /** The line split into one run per game stretch, for colouring. */
  runs: SparkRun[];
  /** High, low and house chip moves pinned to the line. */
  marks: SparkMark[];
  /** One settled movement: a dot, not a line. */
  single: boolean;
  currentLabel: string;
  peakLabel: string;
  troughLabel: string;
  /** Signed change from this line's open (first point) to last. */
  changeLabel: string;
  changePctLabel: string;
  changeTone: 'win' | 'loss' | 'even';
}

/** Session-level quote strip across every series. */
export interface BalanceSessionVM {
  openLabel: string;
  highLabel: string;
  lowLabel: string;
  lastLabel: string;
  changeLabel: string;
  changePctLabel: string;
  changeTone: 'win' | 'loss' | 'even';
  membersLabel: string;
}

export interface BalanceChartVM {
  /** False while the timeline read is in flight. */
  ready: boolean;
  /** Shown instead of the chart before any chip has moved. */
  emptyLine: string | null;
  lines: BalanceChartLineVM[];
  /** Keys for every game on any line; empty when only one game was played. */
  legend: SparkLegendItem[];
  session: BalanceSessionVM | null;
}

const chips = (n: number): string => n.toLocaleString();

const pctOf = (change: number, open: number): string => {
  if (open <= 0) return '—';
  const pct = (change / open) * 100;
  const abs = Math.abs(pct).toFixed(1);
  if (pct > 0) return `+${abs}%`;
  if (pct < 0) return `−${abs}%`;
  return `${abs}%`;
};

const toneOf = (n: number): 'win' | 'loss' | 'even' =>
  n > 0 ? 'win' : n < 0 ? 'loss' : 'even';

export function balanceChartModel(timeline: BalanceTimelineResponse | null): BalanceChartVM {
  if (timeline === null) {
    return { ready: false, emptyLine: null, lines: [], legend: [], session: null };
  }
  // A response the view-model cannot read costs the chart, never the page.
  const series = (Array.isArray(timeline.series) ? timeline.series : []).filter(
    (s) => Array.isArray(s.points) && s.points.length > 0,
  );
  if (series.length === 0) {
    return {
      ready: true,
      emptyLine: 'No chips have moved yet tonight.',
      lines: [],
      legend: [],
      session: null,
    };
  }

  const low = Math.min(...series.map((s) => s.trough));
  const high = Math.max(...series.map((s) => s.peak));
  const openTotal = series.reduce((sum, s) => sum + (s.points[0]?.balance ?? 0), 0);
  const lastTotal = series.reduce((sum, s) => sum + s.current, 0);
  const sessionChange = lastTotal - openTotal;

  const lines = series.map((s) => {
    const open = s.points[0]!.balance;
    const change = s.current - open;
    const spark = sparklineOf(s);
    return {
      memberId: s.memberId,
      name: s.name,
      points: spark.points,
      runs: sparkRunsOf(s, spark.points),
      marks: sparkMarksOf(s, spark.points),
      single: spark.single,
      currentLabel: chips(s.current),
      peakLabel: chips(s.peak),
      troughLabel: chips(s.trough),
      changeLabel: formatSignedChips(change),
      changePctLabel: pctOf(change, open),
      changeTone: toneOf(change),
    };
  });

  return {
    ready: true,
    emptyLine: null,
    lines,
    legend: sparkLegendOf(lines.flatMap((l) => l.runs)),
    session: {
      openLabel: chips(openTotal),
      highLabel: chips(high),
      lowLabel: chips(low),
      lastLabel: chips(lastTotal),
      changeLabel: formatSignedChips(sessionChange),
      changePctLabel: pctOf(sessionChange, openTotal),
      changeTone: toneOf(sessionChange),
      membersLabel: `${series.length} ticker${series.length === 1 ? '' : 's'}`,
    },
  };
}
